<template>
  <v-content :class="{ 'pa-0': preview }">
    <the-loading-overlay :value="showLoadingOverlay" />
    <DocumentChecklistPopout
      v-if="isQuestionnaireDocumentPopupEnabled"
      :async-for-each="asyncForEach"
      :checklist-loading="checklistLoading"
      :current-questionnaire="currentQuestionnaire"
      :documents-uploading="!isEmpty(questionsUploadingDocs)"
      :questions-uploading-docs="questionsUploadingDocs"
      :selected-question="selectedQuestion"
      :show-unanswered="showUnanswered"
      @add-file="addFile"
      @open-answer-later-dialog="(...args) => openAnswerLaterDialog(...args)"
      @open-comment-dialog="(...args) => openCommentDialog(...args)"
      @refresh-questionnaire="refreshQuestionnaire()"
      @save-question="answerQuestion"
      @toggle-not-applicable="toggleNotApplicable"
      @upload="openUploadDialog"
    />

    <!-- Start Dialogs section -->
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <NextQuestionnaireToWorkOnDialog
      :opened.sync="nextQuestionnaireToWorkOnDialogOpened"
      :next-questionnaire="nextQuestionnaire"
      :current-questionnaire="currentQuestionnaire"
    />

    <AnswerLaterDialog
      :opened.sync="answerLaterDialogOpened"
      :selected-question="selectedQuestion"
      @save-answer-later-by="saveAnswerLaterBy"
      :due-date="currentQuestionnaire.due_date"
    />

    <QuestionnaireCompletedConfirmationDialog
      :opened.sync="questionnaireCompletedConfirmationDialogOpened"
      :submission-agreed="submissionAgreed"
      :questionnaire="questionnaire"
      :current-questionnaire="currentQuestionnaire"
      :current-questionnaire-other-documents="
        currentQuestionnaireOtherDocuments
      "
      :documents-still-needed="documentsStillNeeded"
      @confirm="completeQuestionnaire()"
    />

    <ClientEntryDialog
      v-if="currentCompanyId || preview"
      :opened.sync="clientEntryDialogOpened"
      :client-entry-questions="clientEntryQuestions"
      :current-company-id="currentCompanyId"
      @handle-close-entry-dialog="(...args) => handleCloseEntryDialog(...args)"
      @update-parent-question-of-entries="updateParentQuestionOfEntries"
      :is-new-client-entry="isNewClientEntry"
      :preview="preview"
      :parent-question="clientEntryParentQuestion"
      :firm-glossary="firmGlossary"
      :entry-loading="entryLoading"
      :current-questionnaire="currentQuestionnaire"
      :current-company="currentCompany"
      :current-section="currentSection"
    />

    <DocumentChecklistDialog
      :opened.sync="documentChecklistDialogOpened"
      :checklistLoading="checklistLoading"
      :current-questionnaire="currentQuestionnaire"
      :questions-uploading-docs="questionsUploadingDocs"
      :selected-question="selectedQuestion"
      :all-documents-needed="allDocumentsNeeded"
      @upload="openUploadDialog"
    />

    <ContactMethodDialog
      :v-if="!smsCollectionPopupsDisabled"
      :opened.sync="contactInfoDialogOpened"
      :questionnaire="questionnaire"
    />

    <ProfileUpdateDialog
      v-if="currentCompany"
      :opened.sync="profileUpdateDialogOpened"
      :current-company="currentCompany"
      :current-questionnaire="currentQuestionnaire"
      :sms-collection-popups-disabled="smsCollectionPopupsDisabled"
    />

    <CommentDialog
      v-if="currentCompanyId"
      :opened.sync="commentDialogOpened"
      :reason-for-comment="reasonForComment"
      :editable="
        !currentQuestionnaire.locked && !currentQuestionnaire.completed
      "
      :selected-question="selectedQuestion"
      :current-company-id="currentCompanyId"
      @open-conversation="openRelevantConversation"
    />

    <UploadDialog
      :opened.sync="uploadDialogOpened"
      :selected-question="selectedQuestion"
      @select-document-type="selectDocumentType"
      :documents-to-import="documentsToImport"
      @pick-documents="pickDocuments()"
      @import-documents="importDocuments()"
      @add-file="addFile"
    />

    <ConfirmationDialog
      v-model="deleteAllDocumentDialogOpened"
      @confirm="deleteQuestionDocuments"
      @cancel="closedDeleteDocumentsConfirmationDialog"
    >
      {{ $t('questionnaire.removeAllUploadedDocuments') }}
    </ConfirmationDialog>

    <!-- End  Dialogs section -->

    <v-container>
      <div v-if="showUnanswered" class="next-incomplete-question-nav">
        <p>
          Oh no! It looks like you still have some incomplete sections. Let's
          get those completed.
        </p>

        <v-btn
          class="text-none primary--text mx-0"
          @click="goToNextIncompleteSectionViaNav"
        >
          Take me to the next incomplete section
        </v-btn>
      </div>

      <v-layout
        class="section-steps__container mt-0"
        ref="topOfSection"
        :class="{
          'section-steps__container--next-incomplete-question-nav-open': showUnanswered,
          'pt-0': !showUnanswered,
        }"
      >
        <QuestionnaireSidebar
          v-if="!currentQuestionnaire.is_open_item"
          :preview="preview"
          :current-section="currentSection"
          :active-section="activeSection"
          :current-questionnaire="currentQuestionnaire"
          :show-unanswered="showUnanswered"
          :document-checklist-dialog-opened="documentChecklistDialogOpened"
          :build-section-name="buildSectionName"
          :section-has-all-optional-unanswered-questions="
            sectionHasAllOptionalUnansweredQuestions
          "
          @go-to-section="goToSection"
          @open-document-checklist-dialog="openDocumentChecklistDialog"
        >
          <v-btn
            class="text-none primary--text mx-2"
            @click="goToDashboard"
            flat
            v-if="!preview"
          >
            <v-icon color="primary" size="18"> keyboard_arrow_left </v-icon>
            {{ $t('questionnaire.backToDashboard') }}
          </v-btn>
        </QuestionnaireSidebar>

        <v-divider
          vertical
          class="hidden-sm-and-down"
          v-if="!currentQuestionnaire.is_open_item"
        />

        <v-flex
          xs12
          :lg9="!currentQuestionnaire.is_open_item"
          :lg10="currentQuestionnaire.is_open_item"
          offset-md1
        >
          <v-layout class="hidden-md-and-up">
            <v-btn
              class="text-none primary--text ma-0 pl-0"
              @click="goToDashboard"
              :disabled="preview"
              flat
            >
              <v-icon color="primary" size="18"> keyboard_arrow_left </v-icon>
              {{ $t('questionnaire.backToDashboard') }}
            </v-btn>
          </v-layout>
          <v-layout v-if="currentQuestionnaire.is_open_item">
            <v-btn
              class="text-none primary--text mx-2 hidden-sm-and-down"
              @click="goToDashboard"
              flat
            >
              <v-icon color="primary" size="18"> keyboard_arrow_left </v-icon>
              {{ $t('questionnaire.backToDashboard') }}
            </v-btn>
          </v-layout>
          <v-layout column class="text-xs-left mb-3 section-steps__bar">
            <v-layout align-center>
              <v-layout column>
                <v-flex
                  class="font-weight-bold mt-2"
                  :class="
                    $vuetify.breakpoint.mdAndUp ? 'display-1' : 'headline'
                  "
                  v-if="currentQuestionnaire"
                >
                  {{ currentQuestionnaire.tax_year }}
                  {{
                    currentQuestionnaire.is_open_item
                      ? currentQuestionnaire.formatted_name
                      : currentQuestionnaire.name
                  }}
                </v-flex>
              </v-layout>
            </v-layout>
            <v-layout class="hidden-md-and-up">
              <v-select
                :value="mobileSections.find(a => a.id === currentSection.id)"
                @change="goToSection($event)"
                :items="mobileSections"
                item-disabled="disabled"
                class="default-input my-2"
                solo
                hide-details
              >
                <template slot="selection" slot-scope="data">
                  <v-list-tile-content
                    v-if="
                      sectionCompleted(currentQuestionnaire, data.item) &&
                        !sectionHasAllOptionalUnansweredQuestions(
                          currentQuestionnaire,
                          data.item,
                        )
                    "
                  >
                    <v-list-tile-title
                      ><v-icon size="22" class="success--text mr-2"
                        >check</v-icon
                      >{{ buildSectionName(data.item) }}</v-list-tile-title
                    >
                  </v-list-tile-content>

                  <v-list-tile-content v-else-if="showUnanswered">
                    <v-list-tile-title>
                      <v-icon size="22" color="error" class="mr-2"
                        >error_outline</v-icon
                      >
                      {{ buildSectionName(data.item) }}</v-list-tile-title
                    >
                  </v-list-tile-content>

                  <v-list-tile-content v-else>
                    <v-list-tile-title>{{
                      buildSectionName(data.item)
                    }}</v-list-tile-title>
                  </v-list-tile-content>
                </template>
                <template slot="item" slot-scope="data">
                  <v-list-tile-content
                    v-if="
                      sectionCompleted(currentQuestionnaire, data.item) &&
                        !sectionHasAllOptionalUnansweredQuestions(
                          currentQuestionnaire,
                          data.item,
                        )
                    "
                  >
                    <v-list-tile-title
                      ><v-icon size="22" class="success--text mr-3"
                        >check</v-icon
                      >{{ buildSectionName(data.item) }}</v-list-tile-title
                    >
                  </v-list-tile-content>

                  <v-list-tile-content v-else-if="showUnanswered">
                    <v-list-tile-title>
                      <v-icon size="22" color="error" class="mr-3"
                        >error_outline</v-icon
                      >
                      {{ buildSectionName(data.item) }}</v-list-tile-title
                    >
                  </v-list-tile-content>

                  <v-list-tile-content
                    v-else
                    :class="data.item.disabled ? 'darkgrey--text' : ''"
                  >
                    <v-list-tile-title
                      >{{ buildSectionName(data.item)
                      }}{{
                        data.item.disabled ? ' (Locked until submitted)' : ''
                      }}</v-list-tile-title
                    >
                  </v-list-tile-content>
                </template>
              </v-select>
            </v-layout>
          </v-layout>

          <v-layout align-center>
            <v-spacer />
            <v-flex xs12>
              <v-alert
                outline
                :value="true"
                v-if="currentSection.instruction"
                type="info"
                class="mb-4 text-xs-left"
              >
                {{ currentSection.instruction }}
                <template
                  v-if="
                    !isFirmCommunicationDisabled &&
                      sectionKey(currentSection) === 'questionnaire'
                  "
                >
                  {{ $t('alerts.ifYouAreNotSure') }}
                  <span
                    style="cursor: pointer; text-decoration: underline"
                    class="primary--text"
                    @click="openRelevantConversation()"
                  >
                    {{ $t('questionnaire.clickHereToAskAQuestion') }}
                  </span>
                </template>
              </v-alert>

              <!-- Section: Agreement Signature -->
              <AgreementSignatureSection
                v-if="sectionKey(currentSection) === 'agreement_signature'"
                :current-questionnaire="currentQuestionnaire"
                :current-section="currentSection"
                :open-relevant-conversation="openRelevantConversation"
                :preview="preview"
                :is-firm-communication-disabled="isFirmCommunicationDisabled"
                :showUnanswered="showUnanswered"
                @refresh-questionnaire="refreshQuestionnaire()"
                @update-is-signing-agreement="updateIsSigningAgreement"
                :current-company="currentCompany"
              />

              <!-- Section: PDF Signature -->
              <v-layout
                column
                v-else-if="sectionKey(currentSection) === 'pdf_signature'"
              >
              </v-layout>

              <!-- Section: Questionnaire -->
              <v-layout
                column
                v-else-if="sectionKey(currentSection) === 'questionnaire'"
              >
                <span
                  class="caption error--text align-end"
                  style="text-align: end"
                  v-if="!isEmpty(questionsFromCurrentSection)"
                  >* Required</span
                >
                <v-layout
                  column
                  v-for="(question, i) in questionsFromCurrentSection"
                  :key="question.id"
                >
                  <questionnaire-question
                    :question="question"
                    :question-number="
                      currentQuestionnaire.is_open_item
                        ? i + 1
                        : questionNumber(question)
                    "
                    :selectedQuestion="selectedQuestion"
                    :documentsLoading="
                      questionsUploadingDocs.includes(question.id)
                    "
                    :entryLoading="entryLoading"
                    :currentCompanyId="currentCompanyId"
                    :showUnanswered="showUnanswered"
                    :isDocumentsFromLastYearQuestion="
                      question.uploaded_last_year
                    "
                    :allowAnswerLater="!question.disable_answer_later"
                    :current-company="currentCompany"
                    :firm-glossary="firmGlossary"
                    :questionnaire="currentQuestionnaire"
                    :extracted-documents="questionnaireExtractedDocuments"
                    @add-new-entry="addNewEntry"
                    @open-upload-dialog="openUploadDialog"
                    @open-comment-dialog="
                      (...args) => openCommentDialog(...args)
                    "
                    @toggle-not-applicable="toggleNotApplicable"
                    @save-question="answerQuestion"
                    @open-answer-later-dialog="
                      (...args) => openAnswerLaterDialog(...args)
                    "
                    :current-section="currentSection"
                    v-if="
                      question.question_type !== 'upload' ||
                        !showUploadsOnlyInDocumentChecklist
                    "
                  >
                    <template
                      v-slot:entries
                      v-if="
                        question.question_type === 'entries' &&
                          !preview &&
                          !predefinedQuestionAnswers.includes(question.answer)
                      "
                    >
                      <section class="mb-4">
                        <sx-flex :isColumn="true" :gap="2">
                          <div
                            :key="entryNumber"
                            v-for="(entryNumber, i) in getTriggers(question)"
                          >
                            <client-entry-item
                              :name="
                                `${
                                  isSpecialSection(currentSection)
                                    ? findSpecialSection(currentSection)
                                        .itemName
                                    : 'Entry'
                                } ${i + 1}`
                              "
                              :entryName="getEntryName(question, entryNumber)"
                            >
                              <sx-button-group>
                                <div @click="editEntry(question, entryNumber)">
                                  <sx-button
                                    size="tiny"
                                    type="secondary"
                                    :isDisabled="entryLoading"
                                  >
                                    <sx-icon name="edit" size="16" />
                                    <span>Edit</span>
                                  </sx-button>
                                </div>
                                <div
                                  @click="removeEntry(question, entryNumber)"
                                >
                                  <sx-button
                                    size="tiny"
                                    type="secondary"
                                    :isDisabled="entryLoading"
                                  >
                                    <sx-icon name="trash" size="16" />
                                    <span>Delete</span>
                                  </sx-button>
                                </div>
                              </sx-button-group>
                            </client-entry-item>
                          </div>
                        </sx-flex>
                      </section>
                    </template>
                  </questionnaire-question>

                  <v-layout
                    column
                    :key="i"
                    v-for="(followUpQuestion, i) in getFollowUpQuestions(
                      question,
                    )"
                    v-if="question.question_type !== 'entries'"
                  >
                    <questionnaire-question
                      :key="question.id"
                      :question="followUpQuestion"
                      :selectedQuestion="selectedQuestion"
                      :documentsLoading="
                        questionsUploadingDocs.includes(followUpQuestion.id)
                      "
                      :currentCompanyId="currentCompanyId"
                      :showUnanswered="showUnanswered"
                      :allowAnswerLater="!followUpQuestion.disable_answer_later"
                      :current-company="currentCompany"
                      :firm-glossary="firmGlossary"
                      :questionnaire="currentQuestionnaire"
                      :extracted-documents="questionnaireExtractedDocuments"
                      :isDocumentsFromLastYearQuestion="
                        followUpQuestion.uploaded_last_year
                      "
                      @open-upload-dialog="openUploadDialog"
                      @open-comment-dialog="
                        (...args) => openCommentDialog(...args)
                      "
                      @toggle-not-applicable="toggleNotApplicable"
                      @save-question="answerQuestion"
                      @open-answer-later-dialog="openAnswerLaterDialog"
                      @add-new-entry="addNewEntry"
                      :current-section="currentSection"
                      v-if="
                        followUpQuestion.question_type !== 'upload' ||
                          !showUploadsOnlyInDocumentChecklist
                      "
                    >
                      <template
                        v-slot:entries
                        v-if="
                          followUpQuestion.question_type === 'entries' &&
                            !preview &&
                            !predefinedQuestionAnswers.includes(
                              followUpQuestion.answer,
                            )
                        "
                      >
                        <section class="mb-4">
                          <div
                            :key="entryNumber"
                            v-for="(entryNumber, i) in getTriggers(
                              followUpQuestion,
                            )"
                          >
                            <client-entry-item
                              :name="
                                `${
                                  isSpecialSection(currentSection)
                                    ? findSpecialSection(currentSection)
                                        .itemName
                                    : 'Entry'
                                } ${i + 1}`
                              "
                              :entryName="
                                getEntryName(followUpQuestion, entryNumber)
                              "
                            >
                              <sx-button-group>
                                <div
                                  @click="
                                    editEntry(followUpQuestion, entryNumber)
                                  "
                                >
                                  <sx-button
                                    size="tiny"
                                    type="secondary"
                                    :isDisabled="entryLoading"
                                  >
                                    <sx-icon name="edit" size="16" />
                                    <span>Edit</span>
                                  </sx-button>
                                </div>
                                <div
                                  @click="
                                    removeEntry(followUpQuestion, entryNumber)
                                  "
                                >
                                  <sx-button
                                    size="tiny"
                                    type="secondary"
                                    :isDisabled="entryLoading"
                                  >
                                    <sx-icon name="trash" size="16" />
                                    <span>Delete</span>
                                  </sx-button>
                                </div>
                              </sx-button-group>
                            </client-entry-item>
                          </div>
                        </section>
                      </template>
                    </questionnaire-question>
                    <v-layout
                      column
                      :key="i"
                      v-for="(secondFollowUpQuestion,
                      i) in getFollowUpQuestions(followUpQuestion)"
                      v-if="followUpQuestion.question_type !== 'entries'"
                    >
                      <questionnaire-question
                        :key="question.id"
                        :question="secondFollowUpQuestion"
                        :selectedQuestion="selectedQuestion"
                        :documentsLoading="
                          questionsUploadingDocs.includes(
                            secondFollowUpQuestion.id,
                          )
                        "
                        :currentCompanyId="currentCompanyId"
                        :showUnanswered="showUnanswered"
                        :allowAnswerLater="
                          !secondFollowUpQuestion.disable_answer_later
                        "
                        :current-company="currentCompany"
                        :firm-glossary="firmGlossary"
                        :questionnaire="currentQuestionnaire"
                        :extracted-documents="questionnaireExtractedDocuments"
                        :isDocumentsFromLastYearQuestion="
                          secondFollowUpQuestion.uploaded_last_year
                        "
                        @open-upload-dialog="openUploadDialog"
                        @open-comment-dialog="
                          (...args) => openCommentDialog(...args)
                        "
                        @toggle-not-applicable="toggleNotApplicable"
                        @save-question="answerQuestion"
                        @open-answer-later-dialog="openAnswerLaterDialog"
                        :current-section="currentSection"
                        v-if="
                          secondFollowUpQuestion.question_type !== 'upload' ||
                            !showUploadsOnlyInDocumentChecklist
                        "
                      />
                    </v-layout>
                  </v-layout>
                </v-layout>
              </v-layout>

              <!-- Section: Document Checkist -->
              <v-layout
                v-else-if="sectionKey(currentSection) === 'document_checklist'"
              >
                <DocumentChecklistSection
                  v-if="oldDocumentChecklist"
                  :current-questionnaire="currentQuestionnaire"
                  :questions-uploading-docs="questionsUploadingDocs"
                  :selected-question="selectedQuestion"
                  @upload="openUploadDialog"
                  @toggle-not-applicable="toggleNotApplicable"
                  @add-file="addFile"
                  :async-for-each="asyncForEach"
                  @refresh-questionnaire="refreshQuestionnaire()"
                  :checklistLoading="checklistLoading"
                  @save-question="answerQuestion"
                  @open-comment-dialog="(...args) => openCommentDialog(...args)"
                  :show-unanswered="showUnanswered"
                  :documents-uploading="!isEmpty(questionsUploadingDocs)"
                  @open-answer-later-dialog="
                    (...args) => openAnswerLaterDialog(...args)
                  "
                />
                <NewDocumentChecklistSection
                  v-else
                  :current-questionnaire="currentQuestionnaire"
                  :checklist-loading="checklistLoading"
                  :show-unanswered="showUnanswered"
                  :uploadingDocs="questionsUploadingDocs"
                  :current-company="currentCompany"
                  @upload="openUploadDialog"
                  @toggle-not-applicable="toggleNotApplicable"
                  @refresh-questionnaire="refreshQuestionnaire()"
                  @refresh-questionnaire-until-documents-are-processed="
                    refreshQuestionnaireUntilDocumentsAreProcessed()
                  "
                  @add-file="addFile"
                  @open-answer-later-dialog="
                    (...args) => openAnswerLaterDialog(...args)
                  "
                  @save-question="answerQuestion"
                  @open-comment-dialog="(...args) => openCommentDialog(...args)"
                />
              </v-layout>

              <!-- Section: Review and submit -->
              <ReviewAndSubmitSection
                v-else-if="sectionKey(currentSection) === 'review_and_submit'"
                :current-section="currentSection"
                :current-questionnaire="currentQuestionnaire"
                :build-section-name="buildSectionName"
                :section-has-all-optional-unanswered-questions="
                  sectionHasAllOptionalUnansweredQuestions
                "
                @go-to-section="goToSection($event)"
              />

              <!-- Section: Text Return Delivery -->
              <TaxReturnDelivery
                v-else-if="sectionKey(currentSection) === 'delivery'"
                :current-section="currentSection"
                :current-questionnaire="currentQuestionnaire"
                :current-company="currentCompany"
              />

              <!-- Section: Custom -->
              <CustomSection
                v-else
                :current-section="currentSection"
                :current-questionnaire="currentQuestionnaire"
              />
              <v-alert
                :value="true"
                outline
                block
                column
                v-if="shouldDisplayDisclaimer()"
                class="outlined pa-3 text-xs-left confirmation-box"
              >
                <span block class="text-xs-left">
                  {{ $t('questionnaire.bySubmitting') }}
                  {{
                    currentQuestionnaire.is_open_item
                      ? $t('questionnaire.theseItems')
                      : $t('questionnaire.theQuestionnaire')
                  }}:<br />
                  <v-layout align-start style="white-space: pre-wrap">{{
                    currentQuestionnaire.disclaimer
                  }}</v-layout>
                </span>
                <v-layout column align-center>
                  <v-checkbox
                    v-model="submissionAgreed"
                    class="mb-0 mt-2 pa-0 shrink"
                    hide-details
                  >
                    <template slot="label">
                      <span
                        class="error--text font-weight-bold text--darken-1"
                        >{{ $t('questionnaire.iAgree') }}</span
                      >
                    </template>
                  </v-checkbox>
                </v-layout>
              </v-alert>
              <v-layout class="mt-2">
                <v-btn
                  class="text-capitalize primary"
                  block
                  @click="createCheckoutSession"
                  v-if="currentQuestionnaireHasOutstandingBalance"
                >
                  Pay deposit (${{ currentQuestionnaire.deposit_amount }})
                </v-btn>

                <v-btn
                  class="text-capitalize primary"
                  block
                  v-else-if="
                    sectionKey(currentSection) === 'review_and_submit' &&
                      !currentSection.marked_completed
                  "
                  :disabled="
                    !isEmpty(currentQuestionnaire.disclaimer) &&
                      !submissionAgreed
                  "
                  @click="openSubmitConfirmation()"
                >
                  {{ $t('questionnaire.submit') }}
                </v-btn>

                <v-btn
                  class="text-capitalize primary"
                  block
                  @click="proceed"
                  v-else-if="activeSection !== sections.length"
                  :disabled="nextSectionLocked || isSigningAgreement"
                >
                  {{ $t('questionnaire.next') }}
                </v-btn>

                <v-btn
                  class="text-capitalize primary"
                  block
                  :disabled="
                    (sectionKey(currentSection) === 'review_and_submit' &&
                      !isEmpty(currentQuestionnaire.disclaimer) &&
                      !submissionAgreed) ||
                      !allSectionsCompleted ||
                      currentQuestionnaire.is_draft
                  "
                  v-else-if="
                    activeSection === sections.length &&
                      !currentQuestionnaire.completed
                  "
                  :loading="completionLoading"
                  @click="openSubmitConfirmation()"
                >
                  {{ $t('questionnaire.complete') }}
                </v-btn>
              </v-layout>
            </v-flex>
            <v-spacer />
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
import API from '@/api';
import QuestionnaireQuestion from '@/components/QuestionnaireQuestion';
import { mapGetters, mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import has from 'lodash/has';
import moment from 'moment';
import * as Sentry from '@sentry/vue';
import TheLoadingOverlay from '@/components/TheLoadingOverlay';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import SensitiveTypeInput from '@/components/SensitiveTypeInput';
import ContactMethodDialog from '@/components/dialogs/ContactMethodDialog';
import CommentDialog from '@/components/dialogs/CommentDialog';
import ClientEntryDialog from '@/components/dialogs/ClientEntryDialog';
import UploadDialog from '@/components/dialogs/UploadDialog';
import QuestionnaireSidebar from '@/components/QuestionnaireSidebar';
import ProfileUpdateDialog from '@/components/dialogs/ProfileUpdateDialog';
import DocumentChecklistSection from '@/components/sections/DocumentChecklistSection';
import CustomSection from '@/components/sections/CustomSection';
import ReviewAndSubmitSection from '@/components/sections/ReviewAndSubmitSection';
import AgreementSignatureSection from '@/components/sections/AgreementSignatureSection';
import NewDocumentChecklistSection from '@/components/sections/NewDocumentChecklistSection';
import DocumentChecklistPopout from '@/components/DocumentChecklistPopout';
import DocumentChecklistDialog from '@/components/dialogs/DocumentChecklistDialog';
import QuestionnaireCompletedConfirmationDialog from '@/components/dialogs/QuestionnaireCompletedConfirmationDialog';
import NextQuestionnaireToWorkOnDialog from '@/components/dialogs/NextQuestionnaireToWorkOnDialog';
import AnswerLaterDialog from '@/components/dialogs/AnswerLaterDialog';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
// import posthog from 'posthog-js';
import QuestionnaireMixin from '../mixins/QuestionnaireMixin';
import TaxReturnDelivery from '@/components/sections/TaxReturnDelivery';
import ClientEntryItem from '../components/forms/ClientEntryItem.vue';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';

export default {
  name: 'Questionnaire',
  mixins: [UtilsMixins, QuestionnaireMixin, FirmPreferenceMixins],

  components: {
    SensitiveTypeInput,
    ConfirmationDialog,
    ContactMethodDialog,
    CommentDialog,
    ClientEntryDialog,
    UploadDialog,
    QuestionnaireCompletedConfirmationDialog,
    NextQuestionnaireToWorkOnDialog,
    DocumentChecklistDialog,
    QuestionnaireQuestion,
    TheLoadingOverlay,
    QuestionnaireSidebar,
    DocumentChecklistSection,
    DocumentChecklistPopout,
    NewDocumentChecklistSection,
    AnswerLaterDialog,
    ProfileUpdateDialog,
    CustomSection,
    ReviewAndSubmitSection,
    AgreementSignatureSection,
    TaxReturnDelivery,
    ClientEntryItem,
    SxFlex,
    SxIcon,
    SxButton,
    SxButtonGroup,
    ConfirmationDialog,
  },

  props: {
    preview: {
      type: Boolean,
      default: false,
    },
    questionnaire: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      answerLaterDialogOpened: false,
      nextQuestionnaireToWorkOnDialogOpened: false,
      nextQuestionnaire: null,
      profileUpdateDialogOpened: false,
      currentCompanyId: null,
      spouse: null,
      currentCompany: null,
      isNewClientEntry: false,
      clientEntryDialogOpened: false,
      clientEntryParentQuestion: null,
      clientEntryQuestions: null,
      showLoadingOverlay: false,
      questionsUploadingDocs: [],
      submissionAgreed: false,
      completionLoading: false,
      documentsToImport: [],
      uploadDialogOpened: false,
      selectedDocumentType: '',
      questionnaireCompletedConfirmationDialogOpened: false,
      deleteAllDocumentDialogOpened: false,
      currentQuestionnaire: {
        questionnaire_questions: [],
      },
      currentSection: {
        name: '',
      },
      currentSubsection: '',
      selectedQuestion: {
        text: '',
        filtered_questionnaire_comments: [],
      },
      uploadMoreDocuments: false,
      commentDialogOpened: false,
      documentName: '',
      contactInfoDialogOpened: false,
      entryLoading: false,
      documentsStillNeeded: [],
      documentChecklistDialogOpened: false,
      checklistLoading: false,
      showUnanswered: false,
      currentIncompleteSection: null,
      reasonForComment: '',
      engagementLoading: false,
      isSigningAgreement: false,
      foreignPreparerDisclosureLoading: false,
      submitted: false,
      firmGlossary: null,
      editableUser: null,
      refreshCount: 0,
      currentProgress: '',
      currentQuestion: null,
    };
  },
  computed: {
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      currentUser: 'user',
      defaultCompany: 'defaultCompany',
    }),
    ...mapState('companies', ['companies']),
    mobileSections() {
      return this.sections.map(section => {
        return {
          disabled:
            this.sectionKey(section) === 'delivery' &&
            !this.reviewAndSubmitSectionFinished(this.currentQuestionnaire),
          ...section,
        };
      });
    },
    nextSectionLocked() {
      if (this.sections[this.activeSection]) {
        return this.sections[this.activeSection].locked;
      }
    },
    questionsFromCurrentSection() {
      if (this.currentQuestionnaire.is_open_item) {
        return this.currentSection.questionnaire_questions.filter(
          q => !q.follow_up_question_id,
        );
      } else {
        return this.currentSection.questionnaire_questions
          .filter(q => !q.follow_up_question_id)
          .sort((a, b) => a.order - b.order);
      }
    },
    activeSection() {
      if (!isEmpty(this.sections)) {
        return (
          this.sections.findIndex(a => a.id === this.currentSection.id) + 1
        );
      } else {
        return 0;
      }
    },
    currentQuestionnaireOtherDocuments() {
      if (
        this.currentQuestionnaire &&
        this.currentQuestionnaire.extracted_documents
      ) {
        return this.currentQuestionnaire.extracted_documents.filter(document =>
          document.source.toLowerCase().includes(`/other uploads/`),
        );
      }
    },
    currentQuestionnaireHasOutstandingBalance() {
      return (
        this.currentQuestionnaire &&
        this.currentQuestionnaire.deposit_status &&
        this.currentQuestionnaire.deposit_status !== 'paid' &&
        this.currentQuestionnaire.deposit_status !== 'marked_paid' &&
        this.currentQuestionnaire.deposit_status !== 'payment_pending'
      );
    },
    previousQuestionsAnswered() {
      return this.sections
        .filter(s => s.order < this.currentSection.order)
        .every(s => this.sectionCompleted(this.currentQuestionnaire, s));
    },
    allSectionsCompleteExceptReviewAndSubmitSection() {
      return this.sections
        .filter(
          s =>
            this.sectionKey(s) !== 'review_and_submit' &&
            this.sectionKey(s) !== 'delivery',
        )
        .every(s => this.sectionCompleted(this.currentQuestionnaire, s));
    },
    allSectionsCompleted() {
      return this.sections.every(s =>
        this.sectionCompleted(this.currentQuestionnaire, s),
      );
    },
    isQuestionnaireDocumentPopupEnabled() {
      return Boolean(
        this.documentChecklistPopupEnabled &&
          this.currentQuestionnaire.document_popout_checklist_enabled,
      );
    },
    questionnaireExtractedDocuments() {
      return this.currentQuestionnaire.extracted_documents;
    },
    sectionHasAllOptionalUnansweredQuestions() {
      return (questionnaire, section) => {
        return this.isSectionOptionalWithCallback(
          questionnaire,
          section,
          q => q.optional && isEmpty(q.answer),
        );
      };
    },
    buildSectionName() {
      return section => {
        const hasAllOptionalQuestions = this.isSectionOptionalWithCallback(
          this.currentQuestionnaire,
          section,
          q => q.optional,
        );

        if (!hasAllOptionalQuestions) return section.name;

        return `${section.name} (Optional)`;
      };
    },
  },
  methods: {
    moment,
    isEmpty,
    isSpecialSection(section) {
      return [
        'Rental Activity (Schedule E)',
        'Business (Schedule C)',
        'Farm (Schedule F)',
      ].includes(section);
    },
    openAnswerLaterDialog(question, toggleAnswer) {
      this.selectedQuestion = question;
      this.toggleAnswer = toggleAnswer;
      this.answerLaterDialogOpened = true;
    },
    findSpecialSection(section) {
      let specialSection = this.customSections.find(
        s => s.name === section.name,
      );
      if (!specialSection) {
        specialSection = this.customSections.find(s =>
          s.otherNames.includes(section.name),
        );
      }
      return specialSection;
    },
    isSpecialSection(section) {
      return this.customSections
        .map(s => s.name)
        .concat(this.customSections.map(s => s.otherNames).flat())
        .includes(section.name);
    },
    openRelevantConversation() {
      this.commentDialogOpened = false;
      this.$emit(
        'openSpecificConversation',
        this.currentCompany.relevant_conversation_id,
      );
    },
    selectDocumentType(documentType) {
      this.selectedDocumentType = documentType;
    },
    questionHasDocumentsLoading(questionId) {
      return this.questionsUploadingDocs.includes(questionId);
    },
    updateIsSigningAgreement(value) {
      this.isSigningAgreement = value;
    },
    async refreshQuestionnaire() {
      if (!this.preview) {
        const response = await API.questionnaire.getQuestionnaire({
          companyId: this.currentCompanyId,
          id: this.currentQuestionnaire.id,
        });
        this.currentQuestionnaire = response.data;
        this.currentSection = this.sections.find(
          section => section.id === this.currentSection.id,
        );
      }
    },
    async refreshQuestionnaireUntilDocumentsAreProcessed() {
      let done = false;
      let retryCount = 200;

      while (!done && retryCount > 0) {
        await this.sleep(3000);
        this.refreshQuestionnaire();

        done = this.currentQuestionnaire.extracted_documents.some(
          document =>
            moment(document.created_at) > moment().subtract(5, 'minutes') &&
            document.status === 'processing',
        );

        retryCount -= 1;
      }
    },
    async openSubmitConfirmation() {
      if (this.preview) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Submission is disabled in preview mode.',
        });
      }

      this.completionLoading = true;

      if (!this.allSectionsCompleteExceptReviewAndSubmitSection) {
        this.showUnanswered = true;
        this.completionLoading = false;
      } else {
        this.showUnanswered = false;
        if (this.activeSection === this.sections.length) {
          this.questionnaireCompletedConfirmationDialogOpened = true;
        } else {
          this.currentSection.marked_completed = true;
          await API.questionnaire.saveSection({
            companyId: this.currentQuestionnaire.company_id,
            questionnaireId: this.currentQuestionnaire.id,
            sectionId: this.currentSection.id,
            markedCompleted: true,
          });
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have successfully submitted your answers!',
          });
          this.currentSection = this.sections[this.activeSection];
          this.refreshQuestionnaire();
        }
        this.completionLoading = false;
      }
    },
    questionNumber(question) {
      if (this.showUploadsOnlyInDocumentChecklist) {
        return (
          this.currentSection.questionnaire_questions.filter(
            q =>
              q.question_type !== 'upload' &&
              !q.follow_up_question_id &&
              q.section_id === this.currentSection.id &&
              q.order < question.order,
          ).length + 1
        );
      } else {
        return (
          this.currentSection.questionnaire_questions.filter(
            q =>
              !q.follow_up_question_id &&
              q.section_id === this.currentSection.id &&
              q.order < question.order,
          ).length + 1
        );
      }
    },
    getFollowUpQuestionsWithTrigger(question, trigger) {
      return this.currentSection.questionnaire_questions
        .filter(
          q =>
            q.follow_up_question_id === question.id &&
            q.trigger_for_follow_up_question === trigger &&
            q.trigger_for_follow_up_question !== 'Template',
        )
        .sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            return a.order - b.order;
          }

          return a.id - b.id;
        });
    },
    getEntryName(question, entryNumber) {
      const entryQuestions = this.currentSection.questionnaire_questions
        .filter(
          q =>
            q.follow_up_question_id === question.id &&
            q.trigger_for_follow_up_question === entryNumber &&
            q.trigger_for_follow_up_question !== 'Template',
        )
        .sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            return a.order - b.order;
          }

          return a.id - b.id;
        })
        .filter(q => q.question_type === 'text');

      const entryName = entryQuestions
        .filter(q => q.text === 'First name' || q.text === 'Last name')
        .map(q => q.answer)
        .join(' ');

      return entryName !== ''
        ? entryName
        : entryQuestions[0]
        ? entryQuestions[0].answer
        : '';
    },

    async saveAnswerLaterBy(date) {
      this.selectedQuestion.answer_by = date;
      this.selectedQuestion.answer = null;
      await this.saveQuestion(this.selectedQuestion);
      this.answerLaterDialogOpened = false;
    },

    async answerQuestion(question) {
      question.answer_by = '';

      try {
        await this.saveQuestion(question);
      } catch (err) {
        console.error(err);
        Sentry.captureException(err);
      }

      const questionSaveCallback = question.questionSaveCallback;

      if (questionSaveCallback && typeof questionSaveCallback === 'function') {
        questionSaveCallback();
        question.questionSaveCallback = null;
      }
    },

    async saveQuestion(question) {
      let section = this.sections.find(s => s.id === question.section_id);
      section.questionnaire_questions.find(q => q.id === question.id).answer =
        question.answer;

      section.questionnaire_questions.find(
        q => q.id === question.id,
      ).answer_by = question.answer_by;

      if (!this.preview) {
        await API.questionnaire.answerQuestion({
          id: question.id,
          answer: question.answer,
          answerBy: question.answer_by,
          companyId: this.currentCompanyId,
        });

        // posthog.capture('answered question', {
        //   questionnaire_id: this.currentQuestionnaire.id,
        //   question_id: question.id,
        //   question_type: question.question_type,
        // });
      }
    },

    async handleCloseEntryDialog(completedAtLeastOneTab) {
      if (
        !completedAtLeastOneTab ||
        confirm(this.$t('questionnaire.entryDeletionConfirmation'))
      ) {
        this.entryLoading = true;
        if (this.isNewClientEntry && !this.preview) {
          const entryQuestion = this.clientEntryQuestions[0];
          if (entryQuestion && this.clientEntryParentQuestion) {
            const entryNumber = entryQuestion.trigger_for_follow_up_question;
            await this.removeEntry(this.clientEntryParentQuestion, entryNumber);
          }
        }
        this.entryLoading = false;
        this.clientEntryDialogOpened = false;
      }
    },

    // Used in ClientEntryDialog to add a completed checkmark on saveEntries
    // once the parent question is "completed"
    updateParentQuestionOfEntries(parentQuestion) {
      const nextQuestions = this.currentSection.questionnaire_questions;
      const index = nextQuestions.findIndex(
        question => question.id === parentQuestion.id,
      );

      if (index !== -1) {
        nextQuestions[index] = parentQuestion;
        this.currentSection.questionnaire_questions = nextQuestions;
      }
    },

    async addNewEntry(question) {
      this.isNewClientEntry = true;
      this.entryLoading = true;
      if (this.preview) {
        this.clientEntryParentQuestion = question;
        this.clientEntryQuestions = this.currentSection.questionnaire_questions.filter(
          q => q.follow_up_question_id === question.id,
        );
        this.clientEntryDialogOpened = true;
      } else {
        const response = await API.questionnaire.addNewEntry({
          questionnaireQuestionId: question.id,
          companyId: this.currentCompanyId,
        });

        if (response.data[0] && response.data[0].id) {
          this.clientEntryQuestions = response.data;
          this.clientEntryParentQuestion = question;

          this.currentSection.questionnaire_questions = this.currentSection.questionnaire_questions.concat(
            response.data,
          );

          this.updateEntriesQuestion(question);
          this.clientEntryDialogOpened = true;
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
        }
      }

      this.entryLoading = false;
    },
    updateEntriesQuestion(question) {
      question.answer = this.currentSection.questionnaire_questions
        .filter(
          q =>
            q.follow_up_question_id === question.id &&
            q.trigger_for_follow_up_question !== 'Template',
        )
        .map(q => q.trigger_for_follow_up_question)
        .filter(this.onlyUnique)
        .length.toString();
    },

    async editEntry(question, entryNumber) {
      this.isNewClientEntry = false;
      this.clientEntryParentQuestion = question;
      this.clientEntryQuestions = this.getFollowUpQuestionsWithTrigger(
        question,
        entryNumber,
      );
      this.clientEntryDialogOpened = true;
    },

    async removeEntry(question, entryNumber) {
      this.entryLoading = true;

      try {
        const response = await API.questionnaire.removeEntry({
          questionnaireQuestionId: question.id,
          companyId: this.currentCompanyId,
          entryNumber: entryNumber,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
        } else {
          question.answer = response.data.answer;
          this.currentSection.questionnaire_questions = this.currentSection.questionnaire_questions.filter(
            q =>
              q.follow_up_question_id !== question.id ||
              (q.follow_up_question_id === question.id &&
                q.trigger_for_follow_up_question !== entryNumber),
          );
        }
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem.',
        });
        console.log(e);
      } finally {
        this.entryLoading = false;
      }
    },
    getTriggers(question) {
      const triggers = this.currentSection.questionnaire_questions
        .filter(
          q =>
            q.follow_up_question_id === question.id &&
            q.trigger_for_follow_up_question !== 'Template' &&
            !!q.answer,
        )
        .map(q => q.trigger_for_follow_up_question)
        .filter(this.onlyUnique)
        .filter(Boolean);

      return triggers;
    },
    closedDeleteDocumentsConfirmationDialog() {
      this.currentProgress = '';
      this.currentQuestion = null;
      this.deleteAllDocumentDialogOpened = false;
    },
    async openCommentDialog(question, reason, answer) {
      const viewNote = reason ? false : true;
      let openDialog = false;
      if (!viewNote) {
        openDialog = this.checkIfOpenDeleteDocumentConfirmationDialog(question);
      }

      if (openDialog && reason) {
        this.currentProgress = 'open-comment';
        this.currentQuestion = [question, reason, answer];
        this.deleteAllDocumentDialogOpened = true;
      } else {
        this.reasonForComment = reason;
        this.closedDeleteDocumentsConfirmationDialog();
        if (!viewNote) {
          question.answer = answer;
          this.saveQuestion(question);
        }
        this.selectedQuestion = question;
        this.commentDialogOpened = true;
      }
    },
    openUploadDialog(question) {
      if (!question) {
        this.uploadDialogOpened = true;
      }
      if (!this.questionHasDocumentsLoading(question.id)) {
        this.selectedQuestion = question;
        this.documentsToImport = [];
        this.selectedDocumentType =
          this.selectedQuestion.options.length > 1
            ? ''
            : this.selectedQuestion.options[0];
        this.uploadDialogOpened = true;
      }
    },
    getFollowUpQuestions(question) {
      return this.currentSection.questionnaire_questions
        .filter(
          q =>
            q.trigger_for_follow_up_question !== 'Template' &&
            q.follow_up_question_id === question.id &&
            question.answer &&
            (q.trigger_for_follow_up_question === question.answer ||
              (question.answer
                .split('&!&')
                .includes(q.trigger_for_follow_up_question) &&
                question.question_type === 'multiple')),
        )
        .sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            return a.order - b.order;
          }

          return a.id - b.id;
        });
    },
    async goToSection(section) {
      if (this.sectionKey(section) === 'delivery') {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Please complete the previous sections to continue.',
        });
      } else {
        if (!this.questionsUploadingDocs.length) {
          this.documentChecklistDialogOpened = false;
          this.currentSection = section;
          window.scrollTo(0, 0);

          // posthog.capture('went to section', {
          //   questionnaire_id: this.currentQuestionnaire.id,
          //   section: section.name,
          // });
        }
      }
    },
    goToNextIncompleteSectionViaNav() {
      clearTimeout(this.currentFocusTimeout);

      const incompleteRelevantSections = this.sections.filter(section => {
        return (
          this.sectionKey(section) !== 'delivery' &&
          !this.sectionCompleted(this.currentQuestionnaire, section)
        );
      });

      const allIncompleteSectionsExceptReviewAndSubmitAndDelivery = incompleteRelevantSections.filter(
        section => {
          return this.sectionKey(section) !== 'review_and_submit';
        },
      );

      if (
        allIncompleteSectionsExceptReviewAndSubmitAndDelivery.length < 1 ||
        incompleteRelevantSections.length < 1
      ) {
        this.goToSection(this.sections[this.sections.length - 1]);
        this.showUnanswered = false;
        return;
      }

      if (incompleteRelevantSections.length === 1) {
        return this.goToSection(incompleteRelevantSections[0]);
      }

      let index = -1;

      incompleteRelevantSections.find((section, i) => {
        if (!this.currentIncompleteSection) return false;

        if (
          this.currentIncompleteSection &&
          section.id === this.currentIncompleteSection.id
        ) {
          index = i;
          return true;
        }
      });

      let incompleteSection;

      if (!this.currentIncompleteSection) {
        incompleteSection = incompleteRelevantSections[0];
      } else {
        const length = incompleteRelevantSections.length;
        const nextIndex = index >= length - 1 ? 0 : index + 1;
        incompleteSection = incompleteRelevantSections[nextIndex];
      }

      this.currentIncompleteSection = incompleteSection;
      this.goToSection(incompleteSection);

      this.currentFocusTimeout = setTimeout(() => {
        const questionsInDoc = document.querySelectorAll(
          `[data-required-question-unanswered-with-error="true"]`,
        );
        const questionInDoc = questionsInDoc[0];

        if (questionInDoc) {
          const input = questionInDoc.querySelector('input');

          if (input) {
            input.focus();
          } else {
            questionInDoc.focus();
          }

          questionInDoc.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 350); // A short delay before trying to focus
    },
    async openDocumentChecklistDialog() {
      this.documentChecklistDialogOpened = true;

      this.checklistLoading = true;
      if (this.preview) {
        this.allDocumentsNeeded = this.currentQuestionnaire.questionnaire_questions.filter(
          q => q.question_type === 'upload',
        );
      } else {
        const response = await API.questionnaire.getDocumentList({
          companyId: this.currentCompanyId,
          id: this.currentQuestionnaire.id,
        });
        this.allDocumentsNeeded = response.data;
      }
      this.checklistLoading = false;
    },
    checkIfOpenDeleteDocumentConfirmationDialog(question) {
      if (
        question.answer &&
        this.currentProgress === '' &&
        question.question_type === 'upload' &&
        !['Not Applicable', 'Provided Elsewhere', ''].includes(question.answer)
      ) {
        this.deleteAllDocumentDialogOpened = true;
        return true;
      }
      return false;
    },
    deleteQuestionDocuments() {
      switch (this.currentProgress) {
        case 'not-applicable':
          this.toggleNotApplicable(this.currentQuestion);
          break;
        case 'open-comment':
          this.openCommentDialog(
            this.currentQuestion[0],
            this.currentQuestion[1],
            this.currentQuestion[2],
          );
          break;
      }
    },
    async toggleNotApplicable(question) {
      const openDialog = this.checkIfOpenDeleteDocumentConfirmationDialog(
        question,
      );

      if (openDialog) {
        this.currentProgress = 'not-applicable';
        this.currentQuestion = question;
      } else {
        if (question.answer === 'Not Applicable') {
          if (question.question_type === 'entries') {
            this.updateEntriesQuestion(question);
          } else {
            question.answer = '';
          }
        } else {
          question.answer = 'Not Applicable';

          if (question.uploaded_last_year) {
            this.openCommentDialog(
              question,
              'Please briefly explain why the document(s) is no longer applicable.',
              question.answer,
            );
          }
        }
        await this.saveQuestion(question);
        this.closedDeleteDocumentsConfirmationDialog();
      }
    },
    async proceed() {
      if (this.sectionKey(this.currentSection) === 'custom') {
        this.currentSection.marked_completed = true;

        API.questionnaire.saveSection({
          companyId: this.currentQuestionnaire.company_id,
          questionnaireId: this.currentQuestionnaire.id,
          sectionId: this.currentSection.id,
          markedCompleted: true,
        });
      }

      if (this.sectionKey(this.sections[this.activeSection]) === 'delivery') {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Please complete the previous sections to continue.',
        });
      } else {
        this.currentSection = this.sections[this.activeSection];

        window.scrollTo(0, 0);
      }
    },

    async createCheckoutSession() {
      const response = await API.questionnaire.createCheckoutSession({
        id: this.currentQuestionnaire.id,
        companyId: this.currentCompanyId,
      });

      window.location = response.data.checkout_url;
    },

    markQuestionDocUploaded(questionId) {
      this.questionsUploadingDocs = this.questionsUploadingDocs.filter(
        id => id !== questionId,
      );
    },

    async refreshDocumentsUntilProcessed() {
      try {
        const response = await API.questionnaire.getExtractedDocuments({
          companyId: this.currentCompanyId,
          id: this.currentQuestionnaire.id,
        });
        this.currentQuestionnaire.extracted_documents =
          response.data.extracted_documents;
        //Wait 5 seconds before checking if everything has been processed and detailed name has been extracted. Loop this until everything is processed
        this.sleep(5000).then(() => {
          if (
            //Check if there are any documents that have been uploaded in the last 2 minutes (just in case it failed) and are still processing or does not have a detailed name. We will only consider the original document, not the extracted ones that were split
            this.currentQuestionnaire.extracted_documents.some(
              document =>
                moment(document.created_at) > moment().subtract(2, 'minutes') &&
                !document.parent_document_id &&
                (document.status === 'processing' || !document.detailed_name),
            )
          ) {
            console.log('refreshing documents');
            this.refreshDocumentsUntilProcessed();
            this.refreshCount = 5;
          } else {
            if (this.refreshCount > 0) {
              this.refreshDocumentsUntilProcessed();
              this.refreshCount -= 1;
            }
          }
        });
        console.log('checking documents');
      } catch (e) {
        console.log(e);
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async importDocuments() {
      if (this.preview) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Upload is disabled in preview mode.',
        });

        this.uploadDialogOpened = false;
        return;
      }
      const questionId = this.selectedQuestion.id;
      this.questionsUploadingDocs.push(questionId);

      let path;
      let answer =
        this.selectedQuestion.answer === 'Not Applicable' ||
        isEmpty(this.selectedQuestion.answer)
          ? ''
          : this.selectedQuestion.answer;

      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace(
            '{questionnaire_name}',
            !isEmpty(this.currentQuestionnaire.name)
              ? this.currentQuestionnaire.name
              : 'Open Items',
          )
          .replace('{section}', this.currentSection.name);
      }

      try {
        this.uploadDialogOpened = false;
        await this.asyncForEach(
          this.documentsToImport,
          async documentToImport => {
            const trueExtension = await this.getTrueExtension(
              documentToImport.data,
            );
            const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

            if (documentToImport.data.size === 0) {
              throw new Error(
                `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
              );
            }

            if (
              extensionsToCheck.includes(documentToImport.extension) &&
              trueExtension !== documentToImport.extension
            ) {
              throw new Error(
                `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
              );
            }

            if (documentToImport.extension === 'pdf') {
              if (await this.pdfHasPassword(documentToImport.data)) {
                throw new Error(
                  `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
                );
              }
            }

            const {
              data: { id, upload_link },
            } = await API.integration.getTemporaryUploadLink({
              companyId: this.currentCompanyId,
            });

            await API.integration.uploadToDropbox(
              upload_link,
              documentToImport.data,
            );

            const section = this.sections.find(
              section => section.id === this.selectedQuestion.section_id,
            );

            const response = await API.integration.processDocument({
              id,
              documentName:
                documentToImport.name + '.' + documentToImport.extension,
              companyId: this.currentCompanyId,
              path: '/'.concat(path),
              questionnaireId: this.currentQuestionnaire.id,
              selectedOptions: documentToImport.documentType
                ? [documentToImport.documentType]
                : null,
              description: documentToImport.documentType
                ? documentToImport.documentType
                : '',
              tags: section ? [section.name] : [],
            });

            if (response.data.error) {
              console.log(response.data.error);
            } else if (response.data.is_duplicate) {
              this.$store.dispatch('application/setErrorNotification', {
                message: `The document ${documentToImport.name} has already been uploaded before. Thus, you don't need to upload it again.`,
              });
            } else {
              let list = response.data.document_location.split('/');
              list.splice(0, 3);
              answer += '&!&'.concat(list.join('/'));
            }
          },
        );

        const question = this.findSection(
          this.selectedQuestion,
        ).questionnaire_questions.find(question => question.id === questionId);
        question.answer = answer;
        question.answer_by = null;
        if (this.documentChecklistDialogOpened) {
          const document = this.allDocumentsNeeded.find(
            question => question.id === questionId,
          );
          document.answer = answer;
        }
        this.documentsToImport = [];
        await API.questionnaire.answerQuestion({
          id: questionId,
          answer: answer,
          answerBy: null,
          companyId: this.currentCompanyId,
        });
        //wait 3 seconds before refreshing the documents
        this.sleep(3000).then(() => {
          this.refreshDocumentsUntilProcessed();
        });

        // posthog.capture('uploaded document', {
        //   questionnaire_id: this.currentQuestionnaire.id,
        //   question_id: questionId,
        //   source: this.documentChecklistDialogOpened
        //     ? 'documents checklist dialog'
        //     : 'questionnaire',
        // });

        // posthog.capture('answered question', {
        //   questionnaire_id: this.currentQuestionnaire.id,
        //   question_id: question.id,
        //   question_type: question.question_type,
        // });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : 'There was a problem uploading the documents.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.markQuestionDocUploaded(questionId);
      }
    },

    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },

    async goToDashboard() {
      if (this.currentUser.is_advisor) {
        if (this.$route.params.hash) {
          this.$router.push(`/admin-dashboard/${this.$route.params.hash}`);
        } else {
          this.$router.push(
            `/admin-dashboard?companyId=${this.currentCompanyId}`,
          );
        }
      } else {
        if (this.$route.params.hash) {
          this.$router.push(`/${this.$route.params.hash}`);
        } else {
          this.$router.push(`/?companyId=${this.currentCompanyId}`);
        }
      }
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    getExtensionOfFilename(filename) {
      return filename.split('.').pop();
    },

    getNameWithoutExtensionOfFilename(filename) {
      let splitName = filename.split('.');
      splitName.pop();
      return splitName.join('.');
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
      }
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;

      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
      }
    },

    async completeQuestionnaire() {
      try {
        this.showLoadingOverlay = true;

        this.completionLoading = true;
        this.questionnaireCompletedConfirmationDialogOpened = false;
        this.currentQuestionnaire.completed = true;

        const response = await API.questionnaire.submitQuestionnaire({
          companyId: this.currentCompanyId,
          id: this.currentQuestionnaire.id,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
          this.showLoadingOverlay = false;
          this.completionLoading = false;
          this.showUnanswered = true;
        } else {
          this.submitted = true;
          // posthog.capture('submitted questionnaire', {
          //   questionnaire_id: this.currentQuestionnaire.id,
          // });

          if (response.data.questionnaire_id) {
            this.nextQuestionnaire = response.data;
          }
          this.nextQuestionnaireToWorkOnDialogOpened = true;
          this.showLoadingOverlay = false;

          this.completionLoading = false;
        }
      } catch (e) {
        console.log(e);

        try {
          if (e.response.status === 400) {
            await this.refreshQuestionnaire();

            this.showLoadingOverlay = false;
            this.completionLoading = false;
            this.showUnanswered = true;
          } else {
            Sentry.captureMessage(
              'We have an unexpected error in Questionnaire.vue',
              { error: e },
            );
            console.log(e);
          }
        } catch (err) {
          Sentry.captureMessage(
            'We have an unexpected error in Questionnaire.vue',
            { error: err, originalError: e },
          );
          console.log(err);
        }
      }
    },
    async loadFirm() {
      const response = await API.firm.getCurrentUserFirm();

      if (this.firmGlossaryEnabled) {
        const glossaryResponse = await API.firm.getFirmVocabs(response.data.id);
        this.firmGlossary = glossaryResponse.data;
      }
    },
    goToFirstIncompleteSection() {
      if (this.sections.some(a => !a.marked_completed && !this.preview)) {
        this.currentSection = this.sections
          .filter(a => !a.marked_completed)
          .sort((a, b) => a.order - b.order)[0];
      } else {
        this.currentSection = this.sections[0];
      }
    },

    shouldDisplayDisclaimer() {
      return (
        !isEmpty(this.currentQuestionnaire.disclaimer) &&
        this.sectionKey(this.currentSection) === 'review_and_submit' &&
        !this.currentSection.marked_completed &&
        this.currentQuestionnaire.deposit_status !== 'pending'
      );
    },

    isSectionOptionalWithCallback(questionnaire, section, callback) {
      const questionsToBeAnswered = this.filteredSectionQuestionsToBeAnswered(
        questionnaire,
        section,
      );

      if (!questionsToBeAnswered || questionsToBeAnswered.length < 1) {
        return false;
      }

      return questionsToBeAnswered.every(callback);
    },

    async encodeQuestionnaireData() {
      const response = await API.documentDropOff.encodeQuestionnaireData({
        companyId: this.currentQuestionnaire.company_id,
        questionnaireId: this.currentQuestionnaire.id,
        email: this.currentUser.email,
      });
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'You do not have access to this entity.',
        });
        this.$router.push('/');
      } else {
        return response.data.token;
      }
    },
  },

  async created() {
    if (this.preview) {
      this.currentQuestionnaire = this.questionnaire;
      this.currentSection = this.sections[0];
      this.currentCompany = null;
    } else {
      if (this.$route.query.companyId) {
        this.currentCompanyId = parseInt(this.$route.query.companyId);
      } else if (this.$route.params.hash) {
        const hash = this.$route.params.hash;
        const hashId = hash.substring(hash.lastIndexOf('-') + 1);
        const { data } = await API.company.getCompanyIdByHashId(hashId);

        this.currentCompanyId = data.id;
      } else {
        this.currentCompanyId = this.defaultCompany.id;
      }
      if (this.defaultCompany.id !== this.currentCompanyId) {
        await this.$store.dispatch('setDefaultCompany', {
          companyId: this.currentCompanyId,
        });

        await this.$store.dispatch('resetTiles', {
          companyId: this.currentCompanyId,
        });
      }

      const response = await API.company.getCompany(this.currentCompanyId);
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'You do not have access to this entity.',
        });
        this.$router.push('/');
      } else {
        this.currentCompany = response.data;

        const spouseResponse = await API.company.getSpouse(
          this.currentCompanyId,
        );
        this.spouse = spouseResponse.data;

        if (!this.currentUser.is_advisor) {
          if (this.currentUser.readonly_access) {
            this.$router.push('/');
          } else if (this.currentCompany.readonly_access) {
            this.$router.push('/');
          } else if (this.currentCompany.inactive) {
            this.$router.push(`/select-entity`);
          }

          await this.$store.dispatch('resetTiles', {
            hashId: this.currentCompany.hash_id,
            clientName: this.currentCompany.name
              .replaceAll(' ', '-')
              .replaceAll(',', '')
              .replaceAll('#', ''),
          });
        } else {
          await this.$store.dispatch('resetTiles', {
            companyId: this.currentCompanyId,
          });
        }

        try {
          this.$store.commit('application/START_LOADING');

          const response = await API.questionnaire.getQuestionnaire({
            companyId: this.currentCompanyId,
            id: this.$route.query.questionnaireId,
          });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: "The Item doesn't exist",
            });
            if (this.$route.params.hash) {
              this.$router.push(`/${this.$route.params.hash}`);
            } else {
              this.$router.push(`/?companyId=${this.currentCompanyId}`);
            }
          } else {
            this.currentQuestionnaire = response.data;

            if (
              !this.currentUser.is_advisor &&
              this.currentQuestionnaire.is_draft
            ) {
              this.$store.dispatch('application/setErrorNotification', {
                message: 'You do not have access to this questionnaire',
              });
              this.$router.push('/');
            }

            // posthog.group('questionnaire', this.currentQuestionnaire.id, {
            //   company_id: this.currentQuestionnaire.company_id,
            //   engagement_letter: this.currentQuestionnaire.engagement_letter,
            //   engagement_letter_signature: this.currentQuestionnaire
            //     .engagement_letter_signature,
            //   engagement_letter_signed_data: this.currentQuestionnaire
            //     .engagement_letter_signed_date,
            //   engagement_letter_signatures: this.currentQuestionnaire
            //     .engagement_letter_signatures,
            //   sections: this.currentQuestionnaire.sections.map(
            //     section => section.name,
            //   ),
            //   hide_checklist: this.currentQuestionnaire.hide_checklist,
            //   is_open_item: this.currentQuestionnaire.is_open_item,
            //   status: this.currentQuestionnaire.status,
            //   tax_year: this.currentQuestionnaire.tax_year,
            //   total_questions: this.currentQuestionnaire.total_questions,
            //   locked: this.currentQuestionnaire.locked,
            //   completed: this.currentQuestionnaire.completed,
            //   name: this.currentQuestionnaire.name,
            // });

            if (this.currentQuestionnaire.document_drop_off_enabled) {
              const token = await this.encodeQuestionnaireData();
              this.$router.push(`/document-drop-off?token=${token}`);
            }

            if (!this.currentUser.is_advisor) {
              if (this.currentQuestionnaire.update_profile_section) {
                this.profileUpdateDialogOpened = true;
              } else if (
                !this.currentUser.preferred_contact_method &&
                !this.smsCollectionPopupsDisabled
              ) {
                this.contactInfoDialogOpened = true;
              }
            }

            if (
              (this.currentQuestionnaire.locked ||
                this.currentQuestionnaire.completed) &&
              !this.currentUser.is_advisor
            ) {
              this.$store.dispatch('application/setErrorNotification', {
                message: 'This Item is locked for edits.',
              });
              if (this.$route.params.hash) {
                this.$router.push(
                  `/${this.$route.params.hash}?questionnaireId=${this.$route.query.questionnaireId}`,
                );
              } else {
                this.$router.push(
                  `/?companyId=${this.currentCompanyId}&questionnaireId=${this.$route.query.questionnaireId}`,
                );
              }
            } else {
              if (!this.currentQuestionnaire.opened_timestamp) {
                await API.questionnaire.markAsOpened({
                  companyId: this.currentQuestionnaire.company_id,
                  id: this.currentQuestionnaire.id,
                });
              }
              if (this.questionnairePartiallySubmitted) {
                this.showUnanswered = true;
              }
              this.goToFirstIncompleteSection();
              // Start checking if documents have been processed
              if (
                this.currentQuestionnaire.extracted_documents.some(
                  document => document.status === 'processing',
                )
              ) {
                console.log('refreshing documents');
                this.refreshDocumentsUntilProcessed();
              }

              // posthog.capture('opened questionnaire', {
              //   questionnaire_id: this.currentQuestionnaire.id,
              // });
            }
          }
        } catch (e) {
          console.log(e);
        } finally {
          this.$store.commit('application/END_LOADING');
        }
      }
    }

    this.loadFirm();
  },
};
</script>

<style lang="postcss" scoped>
.confirmation-box.outlined {
  margin-top: 16px;
  font-size: 15px;
  border: 1px solid #faa0b2 !important;
  border-radius: 4px;
}
.outlined {
  border: solid 0.2px #b2b2b2 !important;
}
.section {
  &-steps {
    &__container {
      max-width: 1000px;
      margin: 0 auto;

      &--next-incomplete-question-nav-open {
        padding-top: 48px;

        @media (max-width: 960px) {
          padding-top: 36px;
        }

        @media (max-width: 732px) {
          padding-top: 106px;
        }

        @media (max-width: 407px) {
          padding-top: 136px;
        }
      }
    }
    &__sidebar {
      position: sticky;
      top: 100px;
    }
    &__text {
      font-weight: bold;
      margin-left: 6px;
    }
  }
}
.next-incomplete-question-nav {
  width: 100vw;
  background-color: #fa4c4f;
  color: white;
  position: fixed;
  left: calc((100vw - 100%) / 2 * -1);
  margin-top: -35px;
  padding: 8px 25px;
  box-shadow: 0 1px 0 0 var(--gray-200);
  z-index: 7;

  display: flex;
  justify-content: center;
  gap: 16px;

  @media (max-width: 732px) {
    flex-direction: column;
  }

  p {
    text-align: left;
    margin-bottom: 0;
    font-size: 16px;

    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
